/* Base Layout
Author: Maxwell Levene @maxlevene920- Ellmarr
Date: 04/30/2025
Description: Authentication guard component that protects routes from unauthorized
access. Handles redirection and session verification.
*/

import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

interface RouteGuardProps {
  children: React.ReactNode;
}

const RouteGuard: React.FC<RouteGuardProps> = ({ children }) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const location = useLocation();
  
  // If still checking auth status, show minimal loading indicator
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/20">
        <div className="text-center space-y-4">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }
  
  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }
  
  // Check if user has admin role
  if (!user || user.role !== 'admin') {
    return <Navigate to="/restricted" replace />;
  }
  
  return <>{children}</>;
};

export default RouteGuard;
